import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { buildPyramidModel } from '../insights_functions';
import PyramidCardView from './InsightPyramidCardView';
import PyramidTableView from './InsightPyramidTableView';

const CONNECTOR_COLOR = '#b8b8b8';
const CONNECTOR_LABEL_COLOR = '#6b6b6b';
const CONNECTOR_LABEL_VISIBILITY_THRESHOLD = 0.6;

const getFirstLevelBranches = (tree) => {
    if (!tree) {
        return [];
    }

    if (tree.rootNode) {
        return Array.isArray(tree.rootNode.children) ? tree.rootNode.children : [];
    }

    return Array.isArray(tree.rootChildren) ? tree.rootChildren : [];
};

const collapseBranch = (node) => ({
    ...node,
    children: [],
    tableRows: [],
});

const buildRenderedTree = (tree, expandMode, expandedBranchIds) => {
    if (!tree || expandMode === 'none') {
        return tree;
    }

    const expandedBranchIdSet = new Set(expandedBranchIds);
    const mapBranch = (branchNode) => (expandedBranchIdSet.has(branchNode.id) ? branchNode : collapseBranch(branchNode));

    if (tree.rootNode) {
        return {
            ...tree,
            rootNode: {
                ...tree.rootNode,
                children: getFirstLevelBranches(tree).map(mapBranch),
            },
        };
    }

    return {
        ...tree,
        rootChildren: getFirstLevelBranches(tree).map(mapBranch),
    };
};

const getDefaultExpandedBranchIds = (expandMode, branchIds = []) => {
    if (expandMode === 'single') {
        return [];
    }

    if (expandMode === 'multi') {
        return branchIds;
    }

    return [];
};

const estimateLabelWidth = (label = '') => Math.max(42, (label.length * 7) + 12);

const getElementVisibleRatio = (element, containerElement) => {
    if (!element || !containerElement) {
        return 0;
    }

    const elementRect = element.getBoundingClientRect();
    const containerRect = containerElement.getBoundingClientRect();
    const visibleLeft = Math.max(elementRect.left, containerRect.left);
    const visibleRight = Math.min(elementRect.right, containerRect.right);
    const visibleTop = Math.max(elementRect.top, containerRect.top);
    const visibleBottom = Math.min(elementRect.bottom, containerRect.bottom);
    const visibleWidth = Math.max(0, visibleRight - visibleLeft);
    const visibleHeight = Math.max(0, visibleBottom - visibleTop);
    const visibleArea = visibleWidth * visibleHeight;
    const totalArea = Math.max(1, elementRect.width * elementRect.height);

    return visibleArea / totalArea;
};

const collectConnectorSpecs = (node, connectorSpecs = []) => {
    if (!node) {
        return connectorSpecs;
    }

    if (Array.isArray(node.children) && node.children.length > 0) {
        node.children.forEach((child) => {
            connectorSpecs.push({ fromId: node.id, toId: child.id, type: 'node' });
            collectConnectorSpecs(child, connectorSpecs);
        });

        return connectorSpecs;
    }

    if (node.tableRows?.length) {
        connectorSpecs.push({ fromId: node.id, toId: `table__${node.id}`, type: 'table' });
    }

    return connectorSpecs;
};

const collectConnectorLabelSpecs = (node, labelSpecs = []) => {
    if (!node) {
        return labelSpecs;
    }

    if (Array.isArray(node.children) && node.children.length > 0) {
        labelSpecs.push({
            nodeId: node.id,
            label: node.label,
            childIds: node.children.map((child) => child.id),
        });

        node.children.forEach((child) => {
            collectConnectorLabelSpecs(child, labelSpecs);
        });
    }

    return labelSpecs;
};

const renderHorizontalNode = (
    node,
    parentAlignment,
    registerNodeElement,
    registerTableElement,
    expandMode,
    firstLevelBranchIdSet,
    expandedBranchIdSet,
    onBranchToggle,
) => {
    if (!node) {
        return null;
    }

    const hasChildren = Array.isArray(node.children) && node.children.length > 0;
    const alignItems = parentAlignment === 'center' && hasChildren ? 'center' : 'flex-start';
    const isExpandable = expandMode !== 'none' && firstLevelBranchIdSet.has(node.id);
    const isExpanded = isExpandable && expandedBranchIdSet.has(node.id);

    return (
        <div key={node.id} style={{
            display: 'flex',
            alignItems,
            gap: 36,
        }}>
            <div
                ref={(element) => registerNodeElement(node.id, element)}
                data-pyramid-node="1"
                style={{ flex: '0 0 auto' }}
            >
                <PyramidCardView
                    card={node}
                    isClickable={isExpandable}
                    isExpanded={isExpanded}
                    onClick={isExpandable ? () => onBranchToggle(node.id) : undefined}
                />
            </div>
            {hasChildren ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16, flex: '0 0 auto' }}>
                    {node.children.map((child) => renderHorizontalNode(
                        child,
                        parentAlignment,
                        registerNodeElement,
                        registerTableElement,
                        expandMode,
                        firstLevelBranchIdSet,
                        expandedBranchIdSet,
                        onBranchToggle,
                    ))}
                </div>
            ) : node.tableRows?.length ? (
                <div ref={(element) => registerTableElement(`table__${node.id}`, element)} style={{ flex: '0 0 auto' }}>
                    <PyramidTableView rows={node.tableRows} />
                </div>
            ) : null}
        </div>
    );
};

const InsightPyramidView =(props)=>{
    const {viewData, activeOption} = props;
    const scrollContainerRef = useRef(null);
    const contentRef = useRef(null);
    const nodeElementMapRef = useRef(new Map());
    const tableElementMapRef = useRef(new Map());
    const [connectorLayer, setConnectorLayer] = useState({ width: 0, height: 0, paths: [], labels: [] });
    const [expandedBranchIds, setExpandedBranchIds] = useState([]);

    const currentOption = activeOption;// || viewData.DefaultOption;

    const hasChartData = Boolean(
        viewData
        && viewData.ViewData[currentOption]
        && viewData.ViewData[currentOption].ChartData
    );

    const chartData = hasChartData ? viewData.ViewData[currentOption].ChartData : null;
    const chartOptions = viewData?.ChartConfig?.options;
    const chartFormatters = viewData?.ChartConfig?.formatters || [];
    const pyramidModel = useMemo(
        () => (hasChartData ? buildPyramidModel(chartData, chartOptions, chartFormatters) : null),
        [chartData, chartFormatters, chartOptions, hasChartData]
    );
    const pyramidTree = pyramidModel?.tree;
    const parentAlignment = pyramidModel?.options?.parentAlignment || 'top';
    const expandMode = pyramidModel?.options?.expandMode || 'none';
    const showConnectors = pyramidModel?.options?.showConnectors !== false;
    const rawFirstLevelBranchIds = useMemo(
        () => getFirstLevelBranches(pyramidTree).map((branch) => branch.id),
        [pyramidTree]
    );
    const firstLevelBranchKey = rawFirstLevelBranchIds.join('||');
    const firstLevelBranchIds = useMemo(
        () => (firstLevelBranchKey ? firstLevelBranchKey.split('||') : []),
        [firstLevelBranchKey]
    );
    const firstLevelBranchIdSet = useMemo(() => new Set(firstLevelBranchIds), [firstLevelBranchIds]);
    const defaultExpandedBranchIds = useMemo(
        () => getDefaultExpandedBranchIds(expandMode, firstLevelBranchIds),
        [expandMode, firstLevelBranchIds]
    );
    const expandedBranchIdSet = new Set(expandedBranchIds);
    const renderedTree = useMemo(
        () => buildRenderedTree(pyramidTree, expandMode, expandedBranchIds),
        [expandMode, expandedBranchIds, pyramidTree]
    );

    const registerNodeElement = useCallback((nodeId, element) => {
        if (!nodeId) {
            return;
        }

        if (element) {
            nodeElementMapRef.current.set(nodeId, element);
            return;
        }

        nodeElementMapRef.current.delete(nodeId);
    }, []);

    const registerTableElement = useCallback((tableId, element) => {
        if (!tableId) {
            return;
        }

        if (element) {
            tableElementMapRef.current.set(tableId, element);
            return;
        }

        tableElementMapRef.current.delete(tableId);
    }, []);

    const handleBranchToggle = useCallback((branchId) => {
        if (!branchId || expandMode === 'none') {
            return;
        }

        setExpandedBranchIds((previousBranchIds) => {
            const isExpanded = previousBranchIds.includes(branchId);

            if (expandMode === 'single') {
                return isExpanded ? [] : [branchId];
            }

            if (expandMode === 'multi') {
                if (isExpanded) {
                    return previousBranchIds.filter((currentBranchId) => currentBranchId !== branchId);
                }

                return [...previousBranchIds, branchId];
            }

            return previousBranchIds;
        });
    }, [expandMode]);

    const updateConnectorLayer = useCallback(() => {
        if (!contentRef.current || !renderedTree || !showConnectors) {
            setConnectorLayer({ width: 0, height: 0, paths: [], labels: [] });
            return;
        }

        const connectorSpecs = renderedTree.rootNode
            ? collectConnectorSpecs(renderedTree.rootNode)
            : renderedTree.rootChildren.reduce((allSpecs, child) => collectConnectorSpecs(child, allSpecs), []);
        const connectorLabelSpecs = renderedTree.rootNode
            ? collectConnectorLabelSpecs(renderedTree.rootNode)
            : renderedTree.rootChildren.reduce((allSpecs, child) => collectConnectorLabelSpecs(child, allSpecs), []);
        const contentRect = contentRef.current.getBoundingClientRect();
        const width = contentRef.current.scrollWidth;
        const height = contentRef.current.scrollHeight;
        const paths = connectorSpecs
            .map((connectorSpec) => {
                const fromElement = nodeElementMapRef.current.get(connectorSpec.fromId);
                const toElement = connectorSpec.type === 'table'
                    ? tableElementMapRef.current.get(connectorSpec.toId)
                    : nodeElementMapRef.current.get(connectorSpec.toId);

                if (!fromElement || !toElement) {
                    return null;
                }

                const fromRect = fromElement.getBoundingClientRect();
                const toRect = toElement.getBoundingClientRect();
                const startX = fromRect.right - contentRect.left;
                const startY = fromRect.top - contentRect.top + (fromRect.height / 2);
                const endX = toRect.left - contentRect.left;
                const endY = toRect.top - contentRect.top + (toRect.height / 2);
                const elbowOffset = Math.max(18, Math.min(40, (endX - startX) / 2));
                const midX = startX + elbowOffset;

                return `M ${startX} ${startY} L ${midX} ${startY} L ${midX} ${endY} L ${endX} ${endY}`;
            })
            .filter(Boolean);

        const labels = connectorLabelSpecs
            .map((connectorLabelSpec) => {
                const fromElement = nodeElementMapRef.current.get(connectorLabelSpec.nodeId);
                const childElements = connectorLabelSpec.childIds
                    .map((childId) => nodeElementMapRef.current.get(childId))
                    .filter(Boolean);

                if (!fromElement || !childElements.length) {
                    return null;
                }

                const parentVisibleRatio = getElementVisibleRatio(fromElement, scrollContainerRef.current);

                if (parentVisibleRatio >= CONNECTOR_LABEL_VISIBILITY_THRESHOLD) {
                    return null;
                }

                const fromRect = fromElement.getBoundingClientRect();
                const childRects = childElements.map((element) => element.getBoundingClientRect());
                const startX = fromRect.right - contentRect.left;
                const startY = fromRect.top - contentRect.top + (fromRect.height / 2);
                const firstChildX = Math.min(...childRects.map((rect) => rect.left - contentRect.left));
                const childCenterYs = childRects.map((rect) => rect.top - contentRect.top + (rect.height / 2));
                const minChildY = Math.min(...childCenterYs);
                const maxChildY = Math.max(...childCenterYs);
                const elbowOffset = Math.max(18, Math.min(40, (firstChildX - startX) / 2));
                const midX = startX + elbowOffset;
                const labelX = (startX + midX) / 2;
                const labelY = childCenterYs.length > 1
                    ? ((minChildY + maxChildY) / 2)
                    : ((startY + childCenterYs[0]) / 2);

                return {
                    label: connectorLabelSpec.label,
                    x: labelX,
                    y: labelY,
                    width: estimateLabelWidth(connectorLabelSpec.label),
                };
            })
            .filter(Boolean);

        setConnectorLayer({ width, height, paths, labels });
    }, [renderedTree, showConnectors]);

    const handleScroll = useCallback(() => {
        updateConnectorLayer();
    }, [updateConnectorLayer]);

    useEffect(() => {
        nodeElementMapRef.current = new Map();
        tableElementMapRef.current = new Map();
        setConnectorLayer({ width: 0, height: 0, paths: [], labels: [] });
        setExpandedBranchIds(defaultExpandedBranchIds);
    }, [chartData, defaultExpandedBranchIds, expandMode, firstLevelBranchKey]);

    useEffect(() => {
        if (expandMode === 'none') {
            setExpandedBranchIds((previousBranchIds) => (previousBranchIds.length ? [] : previousBranchIds));
            return;
        }

        setExpandedBranchIds((previousBranchIds) => {
            const validBranchIds = previousBranchIds.filter((branchId) => firstLevelBranchIdSet.has(branchId));

            if (!validBranchIds.length) {
                return defaultExpandedBranchIds;
            }

            if (expandMode === 'single' && validBranchIds.length > 1) {
                return [validBranchIds[0]];
            }

            return validBranchIds.length === previousBranchIds.length ? previousBranchIds : validBranchIds;
        });
    }, [defaultExpandedBranchIds, expandMode, firstLevelBranchIdSet, firstLevelBranchKey]);

    useEffect(() => {
        const rafId = window.requestAnimationFrame(() => {
            updateConnectorLayer();
        });

        return () => window.cancelAnimationFrame(rafId);
    }, [renderedTree, updateConnectorLayer]);

    useEffect(() => {
        if (!contentRef.current || typeof ResizeObserver === 'undefined') {
            return undefined;
        }

        const resizeObserver = new ResizeObserver(() => {
            updateConnectorLayer();
        });

        resizeObserver.observe(contentRef.current);

        return () => {
            resizeObserver.disconnect();
        };
    }, [updateConnectorLayer]);

    useEffect(() => {
        const handleWindowResize = () => {
            updateConnectorLayer();
        };

        window.addEventListener('resize', handleWindowResize);

        return () => {
            window.removeEventListener('resize', handleWindowResize);
        };
    }, [updateConnectorLayer]);
    
    if(hasChartData)
    {
        console.log("layout response pyramid view", chartData, pyramidModel);

        return (
            <div style={{
                width: '100%',
                height: '100%',
                minWidth: 0,
                minHeight: 0,
                overflow: 'hidden',
                padding: 12,
                boxSizing: 'border-box',
            }}>
                <div style={{
                    width: '100%',
                    height: '100%',
                    minWidth: 0,
                    minHeight: 0,
                    overflow: 'auto',
                    WebkitOverflowScrolling: 'touch',
                    overscrollBehavior: 'auto',
                }} ref={scrollContainerRef} onScroll={handleScroll}>
                    <div style={{
                        display: 'block',
                        width: 'fit-content',
                        minWidth: 'fit-content',
                        paddingRight: 12,
                        paddingBottom: 12,
                        boxSizing: 'border-box',
                        position: 'relative',
                    }} ref={contentRef}>
                        {connectorLayer.paths.length || connectorLayer.labels.length ? (
                            <svg
                                width={connectorLayer.width}
                                height={connectorLayer.height}
                                style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    pointerEvents: 'none',
                                    overflow: 'visible',
                                    zIndex: 0,
                                }}
                            >
                                {connectorLayer.paths.map((pathData, index) => (
                                    <path
                                        key={`connector-${index}`}
                                        d={pathData}
                                        fill="none"
                                        stroke={CONNECTOR_COLOR}
                                        strokeWidth="1.5"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                ))}
                                {connectorLayer.labels.map((labelSpec, index) => (
                                    <g key={`connector-label-${index}`} transform={`translate(${labelSpec.x}, ${labelSpec.y})`}>
                                        <rect
                                            x={-(labelSpec.width / 2)}
                                            y={-11}
                                            rx="4"
                                            ry="4"
                                            width={labelSpec.width}
                                            height="20"
                                            fill="rgba(255,255,255,0.92)"
                                        />
                                        <text
                                            textAnchor="middle"
                                            dominantBaseline="middle"
                                            fill={CONNECTOR_LABEL_COLOR}
                                            fontSize="11"
                                            fontWeight="600"
                                        >
                                            {labelSpec.label}
                                        </text>
                                    </g>
                                ))}
                            </svg>
                        ) : null}
                        <div style={{ position: 'relative', zIndex: 1 }}>
                        {renderedTree?.rootNode
                            ? renderHorizontalNode(
                                renderedTree.rootNode,
                                parentAlignment,
                                registerNodeElement,
                                registerTableElement,
                                expandMode,
                                firstLevelBranchIdSet,
                                expandedBranchIdSet,
                                handleBranchToggle,
                            )
                            : renderedTree?.rootChildren?.length
                                ? (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                                        {renderedTree.rootChildren.map((child) => renderHorizontalNode(
                                            child,
                                            parentAlignment,
                                            registerNodeElement,
                                            registerTableElement,
                                            expandMode,
                                            firstLevelBranchIdSet,
                                            expandedBranchIdSet,
                                            handleBranchToggle,
                                        ))}
                                    </div>
                                )
                                : null}
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    return null;
}

export default InsightPyramidView
